import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Shield, 
  ArrowLeft,
  Building2,
  Users,
  Scale,
  FileText,
  AlertTriangle,
  Trash2,
  Mail,
  ExternalLink
} from "lucide-react";

const TermsPage = () => {
  const currentDate = "Mars 2026";

  return (
    <div className="min-h-screen bg-[#FFF0F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" style={{ fontFamily: 'Outfit' }}>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center">
              <Shield size={18} className="text-[#FF3366]" fill="#FF3366" />
            </div>
            <span className="text-slate-900">SHARE</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-sm font-medium">
              <FileText size={16} />
              Document juridique
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Conditions Générales d'Utilisation
            </h1>
            <p className="text-slate-500">Version en vigueur : {currentDate}</p>
          </div>

          {/* Warning */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-bold text-amber-800">Information importante</p>
                <p className="text-amber-700 text-sm">
                  SHARE dispose de plusieurs documents juridiques distincts. Ce qui suit est un résumé structuré des éléments essentiels. 
                  En utilisant SHARE, vous acceptez ces conditions.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 1 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Building2 className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">1. Qui est votre interlocuteur ?</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <p className="text-slate-600">
                  Le service RATESHARE est édité par :
                </p>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="font-bold text-slate-900">SAS RATESHARE</p>
                  <p className="text-slate-600 text-sm mt-2">
                    Société par Actions Simplifiée<br />
                    Siège social : Lyon, France
                  </p>
                  <p className="text-slate-600 text-sm mt-2">
                    <span className="font-medium">Président & CEO / DPO :</span> Samy Cherif<br />
                    <span className="font-medium">Contact :</span> contact@rateshare.fr
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Users className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">2. Conditions d'âge et éligibilité</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">2.1. Âge minimum requis</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li><strong>13 ans minimum</strong> pour créer un compte SHARE</li>
                    <li>La vérification de l'âge est effectuée lors de l'inscription</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">2.2. Cas particuliers</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li><strong>Mineurs de 13 à 18 ans :</strong> Vous devez utiliser SHARE sous la supervision d'un parent ou tuteur légal</li>
                    <li><strong>Consentement parental :</strong> Si la loi applicable l'exige, vos parents doivent accepter les conditions en votre nom</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Shield className="text-green-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">3. Ce que vous avez le droit de faire</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <ul className="list-disc list-inside text-slate-600 space-y-2">
                  <li>Utiliser SHARE pour évaluer et être évalué par d'autres utilisateurs</li>
                  <li>Partager des preuves (photos, vidéos) pour appuyer vos évaluations</li>
                  <li>Consulter les profils publics des autres utilisateurs</li>
                  <li>Croiser des personnes à proximité et construire votre réputation</li>
                  <li>Progresser dans les niveaux (Citoyen → Gardien → Légende)</li>
                  <li>Signaler des comportements inappropriés</li>
                  <li>Supprimer votre compte à tout moment</li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 4 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">4. Ce qui est interdit</h2>
            </div>
            <Card className="guardian-card border-red-100">
              <CardContent className="p-6">
                <p className="text-slate-600 mb-4">
                  Les <Link to="/community-guidelines" className="text-[#FF3366] hover:underline font-medium">Règles Communautaires</Link> de SHARE interdisent notamment :
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-2">
                  <li><strong>Évaluations mensongères :</strong> noter quelqu'un sans l'avoir réellement rencontré</li>
                  <li><strong>Contenu illégal :</strong> incitation à la haine, harcèlement, violence, diffamation</li>
                  <li><strong>Fausses preuves :</strong> utiliser des photos/vidéos truquées ou hors contexte</li>
                  <li><strong>Harcèlement et intimidation :</strong> utiliser les évaluations pour nuire à quelqu'un</li>
                  <li><strong>Usurpation d'identité :</strong> créer un faux profil ou se faire passer pour quelqu'un d'autre</li>
                  <li><strong>Manipulation des notes :</strong> créer plusieurs comptes pour gonfler ou diminuer une note</li>
                  <li><strong>Utilisation de robots :</strong> automatisation des interactions</li>
                  <li><strong>Discrimination :</strong> évaluer quelqu'un sur des critères discriminatoires (origine, religion, orientation sexuelle, etc.)</li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 5 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <FileText className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">5. Propriété intellectuelle</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">5.1. Vos contenus vous appartiennent</h3>
                  <p className="text-slate-600">
                    SHARE ne revendique pas la propriété de vos photos, vidéos et évaluations. 
                    Vous restez propriétaire de ce que vous publiez.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">5.2. La licence que vous accordez à SHARE</h3>
                  <p className="text-slate-600 mb-2">
                    En publiant du contenu sur SHARE, vous accordez une licence mondiale, non exclusive, libre de droits pour :
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Héberger, stocker et afficher votre contenu</li>
                    <li>Le distribuer sur la plateforme SHARE</li>
                    <li>Permettre à d'autres utilisateurs de le consulter</li>
                  </ul>
                  <p className="text-slate-500 text-sm mt-2">
                    Cette licence prend fin lorsque vous supprimez votre contenu, dans un délai raisonnable.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 6 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Shield className="text-blue-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">6. Protection des mineurs</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <p className="text-slate-600 mb-4">
                  Conformément au Digital Services Act (DSA) européen, SHARE s'engage à protéger les mineurs :
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Point de contrôle</th>
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Mesure appliquée</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Vérification de l'âge</td>
                        <td className="py-3 px-4 text-slate-900">Déclaration obligatoire à l'inscription</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Protection contre le harcèlement</td>
                        <td className="py-3 px-4 text-slate-900">Système de signalement + modération</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Paramètres par défaut</td>
                        <td className="py-3 px-4 text-slate-900">Comptes protégés par défaut</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Signalement</td>
                        <td className="py-3 px-4 text-slate-900">Système accessible et facile à utiliser</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 7 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="text-red-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">7. Résiliation et suppression de compte</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">SHARE peut :</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Supprimer votre compte si vous violez les Conditions d'utilisation ou les Règles communautaires</li>
                    <li>Modifier ou interrompre tout ou partie des services à tout moment</li>
                    <li>Résilier le contrat sans préavis en cas de non-respect des règles</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">Vous pouvez à tout moment :</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Supprimer votre compte définitivement (via les paramètres)</li>
                    <li>Demander l'effacement de vos données personnelles (RGPD)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 8 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <AlertTriangle className="text-slate-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">8. Limitation de responsabilité</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <p className="text-slate-600 mb-2">SHARE décline toute responsabilité pour :</p>
                <ul className="list-disc list-inside text-slate-600 space-y-1">
                  <li>Les évaluations et contenus publiés par les utilisateurs</li>
                  <li>Les dommages indirects (perte de données, préjudice moral, etc.)</li>
                  <li>Les interruptions de service</li>
                  <li>L'exactitude des informations fournies par les utilisateurs</li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 9 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Scale className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">9. Loi applicable et litiges</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Votre résidence</th>
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Loi applicable</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">France</td>
                        <td className="py-3 px-4 text-slate-900">Droit français + RGPD + DSA</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Union Européenne</td>
                        <td className="py-3 px-4 text-slate-900">Droit français + RGPD + DSA</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Reste du monde</td>
                        <td className="py-3 px-4 text-slate-900">Droit français</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-500 text-sm mt-4">
                  Pour les utilisateurs résidant dans l'UE : le Digital Services Act offre des protections supplémentaires, 
                  notamment le droit de saisir le coordinateur des services numériques de votre pays.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 10 */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Mail className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">10. Documents et contact</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 mb-3">Documents juridiques</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="flex items-center gap-2 text-[#FF3366] hover:underline">
                      <ExternalLink size={16} />
                      Conditions Générales d'Utilisation
                    </Link>
                    <Link to="/community-guidelines" className="flex items-center gap-2 text-[#FF3366] hover:underline">
                      <ExternalLink size={16} />
                      Règles Communautaires
                    </Link>
                    <Link to="/privacy" className="flex items-center gap-2 text-[#FF3366] hover:underline">
                      <ExternalLink size={16} />
                      Politique de Confidentialité
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-2">Contact</h3>
                  <p className="text-slate-600">
                    Pour toute question concernant ces conditions :<br />
                    <a href="mailto:contact@rateshare.fr" className="text-[#FF3366] hover:underline">contact@rateshare.fr</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Back button */}
          <div className="text-center pt-8">
            <Link to="/">
              <Button className="btn-guardian rounded-full">
                <ArrowLeft size={18} className="mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-pink-100 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2026 RATE⭐SHARE - Le GPS de la Bienveillance</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-[#FF3366]">CGU</Link>
            <Link to="/community-guidelines" className="hover:text-[#FF3366]">Règles</Link>
            <Link to="/privacy" className="hover:text-[#FF3366]">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
